<template>
  <div class="messages">
    <breadcrumbs :items="breadcrumbs" />
    <div class="message-item">
      <div class="user">
        <span class="user-avatar">
          <span>
            <sprite name="person"></sprite>
          </span>
        </span>
        <div class="inner">
          <h2 class="send-as">Send as:</h2>
          <span class="user-type">{{authUser.profile.first_name}} {{authUser.profile.last_name}}</span>
        </div>
      </div>
      <compose-message 
        @submit="createMessage"
        :onClick="openModal" 
        :removeFunction="removeRecipient" 
        :recipients="recipients"
        v-model="form"
        />
      <i class="material-icons">more_horiz</i>
    </div>
    <modal :show="show" :onClose="closeModal" :closeIcon="true">
      <div class="modal-header">Select message recipients</div>
      <div class="modal-body">
        <tabs :tabs="tabs" :initialTab="initialTab" v-on:switch-tab="changeTab">
          <template slot="tab-head-users">
            Send to individuals
          </template>
          <template slot="tab-panel-users">
            <div class="recipient-container">
              <span v-for="(recipient, recipientIndex) in recipients" :key="recipientIndex" class="message-recipient">
                {{recipient.name ? recipient.name : recipient}}
                <span @click="removeRecipient(recipientIndex)" class="remove-icon">
                  <rp-sprite name="close"></rp-sprite>
                </span>
              </span>
            </div>
            <rp-table
              ref="usersTable"
              name="users"
              :results="getUsers"
              :headings="[
                {heading: 'User Id', key: 'id'},
                'name',
                'role',
                {heading: '', key: 'action'}
              ]"
            >
              <div
                v-for="(user, index) in users"
                :key="index"
                :slot="`td-${index}-action`">
                <button class="add-recipient" @click="addRecipient(user)">Add recipient</button>
              </div>
            </rp-table>
          </template>

          <template slot="tab-head-groups">
            Send to user group
          </template>
          <template slot="tab-panel-groups">
            <div class="form-group-section">
              <rp-multiple-radio class="form-group"
                v-for="(userGroup, index) in groupRoles"
                :groupName='userGroup.type.charAt(0).toUpperCase() + userGroup.type.slice(1)'
                :icon="userGroup.icon ? userGroup.icon : 'person'"
                :value='selectedGroup'
                :key="index"
                @selectedGroup="setGroup"
                />
              <button class="group-button" @click="addGroup(selectedGroup)">Add recipients</button>
            </div>
          </template>

          <template slot="tab-head-hospitals">
            Send to hospital
          </template>
          <div slot="tab-panel-hospitals">
            <rp-table
              ref="hospitalTable"
              name="hospitals"
              :results="getHospitals"
              :headings="[
                {heading: 'Hospital Id', key: 'id'},
                {heading: 'Hospital Name', key: 'name'},
                {heading: 'Location', key: 'town'},
                {heading: '', key: 'action'}]"
              >
              <div
                v-for="(hospital, index) in hospitals"
                :key="index"
                :slot="`td-${index}-action`">
                <button class="add-recipient" @click="addRecipient(hospital)">Add recipient</button>
              </div>
            </rp-table>
          </div>
        </tabs>
      </div>
    </modal>
    <rp-overlay :active="active">
      <rp-alert
        icon="done"
        :message="successMessage"
        :onClick="successFunction"
      >
      </rp-alert>
    </rp-overlay>
  </div>
</template>

<script>
import Vue from 'vue'
import ComposeMessage from 'raypro_ui/src/components/ui/forms/ComposeMessage'
import Tabs from 'raypro_ui/src/components/ui/Tabs'
import Sprite from 'raypro_ui/src/components/ui/Sprite'
import Table from 'raypro_ui/src/components/ui/Table'
import Overlay from 'raypro_ui/src/components/ui/Overlay'
import Alert from 'raypro_ui/src/components/ui/Alert'
import MultipleRadio from './MultipleRadio'
import Breadcrumbs from '@/components/Breadcrumbs'
import Modal from '@/components/Modal'

import {getAllUsers} from 'raypro_ui/src/api/users'
import {sendMessage} from 'raypro_ui/src/api/messages'
import {getHospitals} from 'raypro_ui/src/api/hospitals'
import {getRoles} from 'raypro_ui/src/api/roles'
import {mapGetters} from 'vuex'

export default {
  components: {
    Breadcrumbs,
    ComposeMessage,
    Sprite,
    Modal,
    Tabs,
    'rp-table': Table,
    'rp-multiple-radio': MultipleRadio,
    'rp-overlay': Overlay,
    'rp-alert': Alert,
    'rp-sprite': Sprite
  },

  name: 'compose-message-page',

  data() {
    return {
      active: false,
      successMessage: 'Your message has been sent!',
      showUsers: true,
      showHospitals: false,
      show: false,
      initialTab: 'users',
      tabs: ['users', 'groups', 'hospitals'],
      groups: [],
      groupRoles: [],
      form: {},
      recipients: [],
      users: [],
      selectedGroup: '',
      hospitals: [],
      groupMessage: false,
      hospitalsTab: false,
      page: {
        totalPages: 1,
        totalEntries: 0,
        pageSize: 10,
        pageNumber: 1
      }
    }
  },

  computed: {
    ...mapGetters(['authUser']),

    messagePayload() {
      const form = this.form

      return {
        message: {
          'sender_id': this.$store.getters.authUser.id,
          'recipients': this.recipients,
          'subject': form.title,
          'body': form.message,
          'important': form.value
        }
      }
    },
    currentBreadcrumb() {
      return this.$route.name
    },

    breadcrumbs() {
      return [{label: this.currentBreadcrumb}]
    }
  },

  methods: {
    successFunction() {
      this.$router.go()
    },

    setGroup(val) {
      this.selectedGroup = val
    },

    addGroup(group) {
      if(group && this.recipients.indexOf(group) === -1) {
        this.recipients.push(group)
        this.groupMessage = true
        this.closeModal()
      }
    },

    openModal() {
      this.show = true
      this.selectedGroup = null
    },

    closeModal() {
      this.show = false
    },

    addRecipient(recipient) {
      if(this.recipients.map((el) => { return el.name }).indexOf(recipient.name) === -1) {
        this.recipients.push({id: recipient.id, name: recipient.name})
      }
    },

    removeRecipient(recipientIndex) {
      this.recipients.splice(recipientIndex, 1)
    },

    getUsers(page) {
      return getAllUsers(page).then(({data, page}) => {
        const mapped = data.map((row) => {
          return {
            id: row.id,
            name: `${row.profile.first_name} ${row.profile.last_name}`,
            email: row.email,
            // TODO: map to age group
            dob: row.profile.dob,
            gender: row.profile.gender,
            role: row.roles[0].name
          }
        })

        this.users = mapped
        return {
          data: mapped,
          page
        }
      })
    },

    getHospitals(page) {
      return getHospitals(page).then(({data, page}) => {
        const mapped = data.map((row) => {
          return {
            'id': row.id,
            'name': row.name,
            'country': row.country.name,
            'town': row.town,
            'address_1': row.address_1,
            'county': row.county
          }
        })

        this.hospitals = mapped
        return {
          data: mapped,
          page
        }
      })
    },

    createMessage() {
      if(this.hospitalsTab) {
        Vue.set(this.messagePayload, 'group', 'hospitals')
      } else if(this.groupMessage) {
        Vue.set(this.messagePayload, 'group', (this.selectedGroup.charAt(0).toLowerCase() + this.selectedGroup.slice(1)).split(' ').join('_'))
      }

      sendMessage(this.messagePayload)
        .then(() => {
          this.active = true
        })
        .catch(({response}) => {
          // TODO: map errors to flat structure for form
          this.errors = response.errors
        })
    },

    changeTab(tabName) {
      this.recipients = []
      this.selectedGroup = null
      if(tabName === 'hospitals') {
        this.hospitalsTab = true
      } else {
        this.hospitalsTab = false
      }
    }
  },
  mounted() {
    getRoles().then(({data}) => {
      data.map((role) => {
        return this.groups.push({
          type: role.name,
          value: role.id
        })
      })
      this.groups.forEach((item) => {
        if(item.type === 'surgeon') {
          item.icon = 'surgeon'
          return item.icon
        }
        if(item.type === 'nurse') {
          item.icon = 'nurse'
          return item.icon
        }
        if(item.type === 'raypro_admin') {
          item.type = item.type.split('_').join(' ')
          item.icon = 'person'
          return item.type, item.icon
        }
        if(item.type === 'hospital_admin') {
          item.type = item.type.split('_').join(' ')
          item.icon = 'hospital'
          return item.type, item.icon
        }
      })
      this.groupRoles = this.groups.filter((item) => {
        return item.type !== 'patient'
      })
    })
  }
}
</script>

<style lang="sass" scoped>
  @import 'raypro_ui/src/styles/base/_variables.sass'
  @import 'raypro_ui/src/styles/helpers/_mixins.sass'
  @import '../../../stylesheets/helpers/mixins'

  .messages
    color: $color-text

    .message-item
      display: flex
      align-items: flex-start
      flex-direction: column
      @include media($media-medium)
        flex-direction: row
      .user
        display: flex
        align-items: center
        margin-bottom: 30px
        @include media($media-medium)
          width: 45rem
          margin: 0 30px
          padding: 2.5rem 0
          border-bottom: 1px solid $color-border

        .user-avatar
          width: 5rem
          height: 5rem
          margin-right: 1.5rem
          fill: $color-panel-bg
          font-size: 1.8rem
          font-weight: $font-light
          border-radius: 50%
          display: flex
          align-items: center
          justify-content: center
          background: $color-side-nav
          .sprite
            width: 3rem

        .inner
          color: $color-text-invert
          display: flex
          flex-direction: column
          .send-as
            font-size: 1.5rem
            font-weight: $font-bold
            margin: 0 15px 0 0
            text-transform: uppercase

            @include media($media-medium)
              margin-bottom: .8rem

          .user-type
            font-size: 2rem
            font-weight: $font-light

      .compose-message
        width: 100%
        @include media($media-medium)
          margin: 0 2.8rem

      > i
        cursor: pointer
        padding: .5rem 0
        font-size: 30px

    .modal-header
      padding: 20px
      font-size: 14px

    .modal-body
      .recipient-container
        display: flex
        flex-direction: row
        flex-wrap: wrap;
        margin-bottom: 30px
        min-height: 32px
        .message-recipient
          margin-bottom: 10px
          margin-right: 10px
          padding: 5px 10px
          background: $color-grey-light
          border-radius: 25px
          .remove-icon
            cursor: pointer
            margin-left: 5px
            .sprite
              width: 10px
              height: 10px
      min-height: 700px
      .form-group-section
        display: flex
        align-items: center
        justify-content: space-between
        flex-wrap: wrap
        .group-button
          position: absolute
          bottom: 20px
          right: 20px
          padding: 0px 20px
          height: 30px
          font-size: 11px
          background: $color-primary
          color: $color-button-text
          border-radius: 3px
          border: 0
          text-transform: uppercase
          font-weight: $font-light
          outline: none
          cursor: pointer
      .add-recipient
        color: $color-primary
        border: 0
        background: transparent
        text-transform: uppercase
        outline: none
        cursor: pointer
</style>



// WEBPACK FOOTER //
// src/components/pages/Messages/ComposeMessage.vue